import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

/**
 * "Otros Siniestros": historial del siniestro por póliza y por patente.
 *
 * Dos problemas que resuelve este hook:
 *
 * 1) Nros de póliza reutilizados. Se empezaron a reusar números de pólizas viejas, así que
 *    `porPoliza` puede traer siniestros de un titular/vehículo anterior que no tienen nada que
 *    ver con el actual. Se toma siempre la ÚLTIMA MENCIÓN de la póliza: el bloque de vigencia
 *    más reciente (al que pertenece el siniestro actual). Lo anterior no se descarta en silencio,
 *    queda aparte en `polizaAnterior` para poder consultarlo.
 *
 * 2) La consulta rompía. Si el backend tarda o falla, el componente quedaba con el spinner
 *    infinito porque el catch solo logueaba. Acá se corta por timeout y se expone `error` + `recargar`.
 */

const TIMEOUT_MS = 45000;

function tiempo(valor) {
  if (!valor) return null;
  const t = new Date(valor).getTime();
  return Number.isNaN(t) ? null : t;
}

/** Fecha con la que se ordena una denuncia: inicio de vigencia y, si no hay, la fecha del stro. */
function fechaOrden(d) {
  return tiempo(d?.qnx?.ados_fech_ini) ?? tiempo(d?.fechaStro) ?? 0;
}

/** Normaliza patentes para comparar (vienen con mayúsculas/minúsculas mezcladas, ej "OTM312"/"otm312"). */
function normDom(dom) {
  return String(dom || '')
    .trim()
    .toUpperCase();
}

/**
 * Separa los siniestros de una póliza entre la vigencia actual y las menciones anteriores
 * del mismo número.
 *
 * Criterio: la "mención actual" es la del siniestro que se está viendo. Pertenecen a ella los
 * siniestros de la misma patente, y también los que se solapan con su vigencia (cubre el caso
 * de cambio de unidad conservando la póliza). Un siniestro de otra patente cuya vigencia terminó
 * antes de que empezara la actual es una reutilización del número: va a `polizaAnterior`.
 */
export function separarPorPoliza(porPoliza, actual) {
  const lista = Array.isArray(porPoliza) ? porPoliza : [];
  const domActual = normDom(actual?.dom);
  const iniActual = tiempo(actual?.qnx?.ados_fech_ini);

  const vigentes = [];
  const anteriores = [];

  lista.forEach(d => {
    const mismaPatente = domActual && normDom(d.dom) === domActual;
    if (mismaPatente) {
      vigentes.push(d);
      return;
    }
    // Sin fechas para comparar no se puede afirmar que sea otra póliza: se muestra.
    const finOtro = tiempo(d?.qnx?.ados_fech_fin);
    if (iniActual === null || finOtro === null) {
      vigentes.push(d);
      return;
    }
    if (finOtro < iniActual) anteriores.push(d);
    else vigentes.push(d);
  });

  const porFechaDesc = (a, b) => fechaOrden(b) - fechaOrden(a);
  return { vigentes: vigentes.sort(porFechaDesc), anteriores: anteriores.sort(porFechaDesc) };
}

/** Saca repetidos por id y ordena de más nuevo a más viejo. */
function limpiar(lista) {
  const vistos = new Set();
  return (Array.isArray(lista) ? lista : [])
    .filter(d => {
      if (!d || d.id == null || vistos.has(d.id)) return false;
      vistos.add(d.id);
      return true;
    })
    .sort((a, b) => fechaOrden(b) - fechaOrden(a));
}

export default function useOtrasDenuncias(id) {
  const [denuncia, setDenuncia] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [intento, setIntento] = useState(0);

  const recargar = useCallback(() => setIntento(n => n + 1), []);

  useEffect(() => {
    if (!id) return undefined;
    let vivo = true;
    const fuente = axios.CancelToken.source();

    setCargando(true);
    setError(null);

    axios
      .get(`https://back.agsseguros.online/api/siniestros/${id}`, { timeout: TIMEOUT_MS, cancelToken: fuente.token })
      .then(res => {
        if (!vivo) return;
        setDenuncia(res.data);
        setCargando(false);
      })
      .catch(err => {
        if (!vivo || axios.isCancel(err)) return;
        console.error('Error al buscar otros siniestros', err);
        setDenuncia(null);
        setError(
          err.code === 'ECONNABORTED' || err.response?.status === 504
            ? 'La consulta de historial tardó demasiado y se canceló. Suele pasar con pólizas reutilizadas.'
            : err.response?.data?.message || err.message || 'No se pudo cargar el historial.'
        );
        setCargando(false);
      });

    return () => {
      vivo = false;
      fuente.cancel();
    };
  }, [id, intento]);

  const datos = useMemo(() => {
    const otras = denuncia?.otrasDenuncias || {};
    const propio = d => d.id !== denuncia?.id;

    const poliza = limpiar(otras.porPoliza).filter(propio);
    const { vigentes, anteriores } = separarPorPoliza(poliza, denuncia);

    // El endpoint no devuelve nroStro en el nivel raíz: se recupera del propio historial.
    const yoEnHistorial =
      [...(otras.porPoliza || []), ...(otras.porPatente || [])].find(d => d?.id === denuncia?.id) || null;

    return {
      actual: denuncia ? { ...denuncia, nroStro: denuncia.nroStro ?? yoEnHistorial?.nroStro ?? null } : null,
      porPoliza: vigentes,
      polizaAnterior: anteriores,
      porPatente: limpiar(otras.porPatente).filter(propio),
    };
  }, [denuncia]);

  return { ...datos, cargando, error, recargar };
}
